import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FiX, FiCopy, FiArrowRight, FiRefreshCw, FiAlertTriangle, FiCheck } from "react-icons/fi";
import { fetchTaskEvents, copyTasksToEvent, moveTasksToEvent } from "../../../utils/taskApi";

/**
 * Put selected tasks into an event — by copying them, or by moving them.
 *
 * The two are genuinely different and are easy to confuse, so the dialog says
 * which one it is doing in every place it can:
 *
 *   **Copy** leaves the originals alone and creates independent duplicates.
 *   Reusing last event's best tasks must not mean that editing one of them
 *   rewrites history in the event it already ran in — and a duplicate is what
 *   lets a user who did the original do the copy too, and be paid again.
 *
 *   **Move** re-files the same tasks. Nothing is duplicated. This is what an
 *   admin wants for tasks that belong to no event yet and are therefore
 *   invisible in the app.
 *
 * Only copy can leave a mess behind, which is why it is the one spelled out.
 */

const STATUS_BADGE = {
  running: "bg-emerald-50 text-emerald-700 border-emerald-200",
  coming_soon: "bg-blue-50 text-blue-700 border-blue-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-gray-100 text-gray-500 border-gray-200",
};

const STATUS_LABEL = {
  running: "Running now",
  coming_soon: "Coming soon",
  draft: "Draft",
  completed: "Finished",
};

const COPY = {
  title: (n) => `Copy ${n} task${n === 1 ? "" : "s"} to an event`,
  hint: "The originals stay where they are. Each copy is a separate task.",
  action: "Copy",
  busy: "Copying...",
  icon: FiCopy,
  outcome: (name) =>
    `Copies land in "${name}" as new, editable tasks — each keeps its country targeting.`,
  fallbackError: "Failed to copy the tasks",
};

const MOVE = {
  title: (n) => `Move ${n} task${n === 1 ? "" : "s"} to an event`,
  hint: "The same tasks, re-filed. Nothing is duplicated.",
  action: "Move",
  busy: "Moving...",
  icon: FiArrowRight,
  outcome: (name) => `These tasks will belong to "${name}" and appear while it is running.`,
  fallbackError: "Failed to move the tasks",
};

const CopyToEventModal = ({ isOpen, onClose, tasks = [], onCopied, mode = "copy" }) => {
  const copy = mode === "move" ? MOVE : COPY;
  const Icon = copy.icon;
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedEvent(null);

    fetchTaskEvents()
      .then((res) => {
        if (!cancelled) setEvents(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load events");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!selectedEvent) return;
    setCopying(true);
    try {
      const ids = tasks.map((task) => task.id);
      const res =
        mode === "move"
          ? await moveTasksToEvent(ids, selectedEvent.id)
          : await copyTasksToEvent(ids, selectedEvent.id);
      toast.success(res.message || "Done");
      if (onCopied) onCopied(res.data);
      onClose();
    } catch (err) {
      // The API refuses for reasons worth reading verbatim — every selected
      // task already being in that event, for one.
      toast.error(err.message || copy.fallbackError);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <Icon size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">{copy.title(tasks.length)}</h2>
              <p className="text-[11px] text-gray-400">{copy.hint}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition">
            <FiX size={17} />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-100">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            {mode === "move" ? "Moving" : "Copying"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {tasks.slice(0, 8).map((task) => (
              <span
                key={task.id}
                className="text-[11px] bg-gray-50 border border-gray-200 text-gray-700 rounded-full px-2.5 py-0.5"
              >
                {task.title}
              </span>
            ))}
            {tasks.length > 8 && (
              <span className="text-[11px] text-gray-400 px-1 py-0.5">+{tasks.length - 8} more</span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            {mode === "move" ? "Move into" : "Copy into"}
          </p>

          {loading && (
            <div className="py-10 text-center text-gray-400 text-xs flex flex-col items-center gap-2">
              <FiRefreshCw className="animate-spin text-blue-600" size={18} />
              Loading events...
            </div>
          )}

          {error && !loading && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={15} />
              <p className="text-xs text-amber-800">{error}</p>
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <p className="py-10 text-center text-xs text-gray-400">
              There are no events yet. Create one first, then copy tasks into it.
            </p>
          )}

          {!loading && events.length > 0 && (
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
              {events.map((event) => {
                const selected = selectedEvent && selectedEvent.id === event.id;
                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => setSelectedEvent(event)}
                    aria-pressed={selected}
                    className={`w-full text-left px-4 py-3 transition flex items-center justify-between gap-3 ${
                      selected ? "bg-blue-50/70" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{event.name}</p>
                      <p className="text-[11px] text-gray-400">
                        {event.task_count} task{event.task_count === 1 ? "" : "s"} already
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                          STATUS_BADGE[event.status] || STATUS_BADGE.draft
                        }`}
                      >
                        {STATUS_LABEL[event.status] || event.status}
                      </span>
                      {selected && <FiCheck size={15} className="text-blue-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400">
            {selectedEvent ? copy.outcome(selectedEvent.name) : "Pick an event."}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              onClick={handleCopy}
              disabled={copying || !selectedEvent || tasks.length === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
            >
              {copying ? copy.busy : copy.action}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyToEventModal;
